import React, { useState, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
    Paper, Typography, TablePagination, TextField, Box, TableSortLabel, Dialog, 
    DialogTitle, DialogContent, DialogActions, IconButton,Button, Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import PayslipZip from "../../../../../scenes/payroll/payslipZip.js";
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme.js";
import axios from "axios";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';

const AppEmpList = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [employees, setEmployees] = useState([]);
    const [totalEmployees, setTotalEmployees] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("First_Name");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const storedUser = JSON.parse(localStorage.getItem("emsUser"));
    const token = storedUser?.user.stsTokenManager?.accessToken;
    const [expandedRowId, setExpandedRowId] = useState(null);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
              
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL_HRM}/get-employees-list`, {
                    headers: {
                      Authorization: `Bearer ${token}`
                    }
                  });
                setEmployees(response.data.employees);
                setTotalEmployees(response.data.count || response.data.employees.length);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
                console.error("Error fetching employees:", err);
            }
        };

        fetchEmployees();
    }, []);

    const handleOpenDialog = () => {
        if (selectedEmployees.length === 0) {
            alert("Please select at least one employee before downloading.");
        } else {
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSelectEmployee = (id) => {
        setSelectedEmployees((prev) =>
            prev.includes(id)
                ? prev.filter(empId => empId !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedEmployees(employees.map(emp => emp.id));
        } else {
            setSelectedEmployees([]);
        }
    };

    const isAllSelected = employees.length > 0 && selectedEmployees.length === employees.length;

    const handleViewDetails = (id) => {
        localStorage.setItem("selectedEmployeeId", id);
        navigate(`/employee/${id}`);
    };

    const handleEditEmployee = (id, email) => {
        localStorage.setItem("editEmployeeId", id);
        localStorage.setItem("payslipEmployeeId", id);
        localStorage.setItem("payslipEmployeeemailidupdated", email);
        navigate(`/edit-employee/${id}`);
    };

    const handlePayslipEmployee = (id, email) => {
        localStorage.setItem("payslipEmployeeId", id);
        localStorage.setItem("payslipEmployeeemail", email);
        navigate(`/employee/${id}/payslip`);
    };

    const handleDeleteEmployee = async (id) => {
        if (window.confirm("Are you sure you want to delete this employee?")) {
            try {
                if (!token) {
                    console.error("Access token missing.");
                    alert("User not authenticated.");
                    return;
                }
    
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL_HRM}/delete-employee/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setEmployees(prevEmployees => prevEmployees.filter(emp => emp.id !== id));
                setTotalEmployees(prev => prev - 1);
                setSelectedEmployees(prev => prev.filter(empId => empId !== id));
            } catch (error) {
                console.error("Error deleting employee:", error);
                alert("Failed to delete employee. Please try again.");
            }
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const sortedEmployees = [...employees].sort((a, b) => {
        if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
        return 0;
    });

    const filteredEmployees = sortedEmployees.filter((emp) =>
        Object.values(emp).some(value =>
            value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>Loading employees...</Typography>;
    }

    if (error) {
        return <Typography variant="h6" color="error" sx={{ textAlign: "center", mt: 4 }}>Error: {error}</Typography>;
    }

    return (
        <Box sx={{
            maxWidth: "95%",
            margin: "auto",
            mt: 4,
            p: 3,
            borderRadius: "4px"
        }}>
            <Typography variant="h2" sx={{
                textAlign: "center",
                fontWeight: "bold",
                mb: 2,
                color: colors.blueAccent[500]
            }}>
                Employee List
            </Typography>

            <Typography variant="h5" sx={{
                textAlign: "center",
                mb: 2,
                fontWeight: "bold",
                color: colors.grey[100]
            }}>
                Total Employees: {totalEmployees}
            </Typography>

            <TextField
                label="Search by Name"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                    mb: 2,
                    "& .MuiInputLabel-root": {
                    color: "text.primary",
                    fontWeight: "bold",
                    fontSize: "18px",
                    transform: "translate(14px, -9px) scale(0.75)",
                    transformOrigin: "top left",
                    "&.Mui-focused": {
                        color: "text.primary",
                    },
                    },
                    "& .MuiOutlinedInput-root": {
                    fontWeight: "bold",
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: "transparent",
                    "& fieldset": {
                        border: "1px solid gray",
                    },
                    "&:hover fieldset": {
                        border: "1px solid gray",
                    },
                    "&.Mui-focused fieldset": {
                        border: "1px solid gray",
                    },
                    },
                    "& .MuiInputBase-input": {
                    color: "text.primary",
                    },
                }}
                placeholder="Enter..."
                onChange={(e) => setSearchQuery(e.target.value)}
            />
            <TableContainer component={Paper} elevation={4} sx={{
                borderRadius: 2,
                maxHeight: 1500,
                backgroundColor: colors.primary[400]
            }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                    sx={{ color: colors.greenAccent[500] }}
                                />
                            </TableCell>
                            {["First_Name", "Last_Name", "Mobile_Number", "Corporate_Email_ID", "Designation", "Joining_Date"].map((column) => (
                                <TableCell key={column} sx={{
                                    color: colors.grey[100],
                                    fontWeight: "bold",
                                    pl: 3
                                }}>
                                    <TableSortLabel
                                        active={orderBy === column}
                                        direction={orderBy === column ? order : "asc"}
                                        onClick={() => handleRequestSort(column)}
                                        sx={{
                                            color: `${colors.grey[100]} !important`,
                                            "& .MuiTableSortLabel-icon": { color: `${colors.grey[100]} !important` }
                                        }}
                                    >
                                        {column.replace("_", " ")}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell sx={{
                                color: colors.grey[100],
                                fontWeight: "bold"
                            }}>
                               Status
                                <button
                                    onClick={handleOpenDialog}
                                    style={{
                                        backgroundColor: colors.blueAccent[600],
                                        color: colors.grey[100],
                                        border: "none",
                                        padding: "5px 10px",
                                        cursor: "pointer",
                                        borderRadius: "5px",
                                        marginLeft: "15px",
                                    }}
                                >
                                    Download ZIP
                                </button>
                                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md" PaperProps={{
                                    style: {
                                        backgroundColor: colors.primary[400],
                                        color: colors.grey[100]
                                    }
                                }}>
                                    <DialogTitle sx={{
                                        fontWeight: "bold",
                                        mb: 2,
                                        color: colors.blueAccent[500],
                                        backgroundColor: colors.primary[500]
                                    }}>
                                        Download Payslip ZIP
                                        <IconButton
                                            aria-label="close"
                                            onClick={handleCloseDialog}
                                            sx={{
                                                position: "absolute",
                                                right: 8,
                                                top: 8,
                                                color: colors.grey[100]
                                            }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent sx={{ backgroundColor: colors.primary[400] }}>
                                        <PayslipZip selectedEmployees={selectedEmployees} />
                                    </DialogContent>
                                </Dialog>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                    {filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((emp) => (
                        <React.Fragment key={emp.id}>
                        <TableRow
                            sx={{
                            backgroundColor: colors.primary[400],
                            color: colors.grey[100],
                            "&:hover": {
                                backgroundColor: colors.grey[800],
                            },
                            }}
                            onClick={(e) => {
                            if (e.target.type !== "checkbox") {
                                setExpandedRowId(expandedRowId === emp.id ? null : emp.id);
                            }
                            }}
                        >
                            <TableCell padding="checkbox">
                            <Checkbox
                                sx={{ color: colors.greenAccent[500] }}
                                checked={selectedEmployees.includes(emp.id)}
                                onChange={(event) => {
                                event.stopPropagation();
                                handleSelectEmployee(emp.id);
                                }}
                            />
                            </TableCell>
                            <TableCell sx={{ pl: 3, color: colors.grey[100] }}>{emp.First_Name || "N/A"}</TableCell>
                            <TableCell sx={{ pl: 3, color: colors.grey[100] }}>{emp.Last_Name || "N/A"}</TableCell>
                            <TableCell sx={{ pl: 3, color: colors.grey[100] }}>{emp.Mobile_Number || "N/A"}</TableCell>
                            <TableCell sx={{ pl: 3, color: colors.grey[100] }}>{emp.Corporate_Email_ID || "N/A"}</TableCell>
                            <TableCell sx={{ pl: 3, color: colors.grey[100] }}>{emp.Designation || "N/A"}</TableCell>
                            <TableCell sx={{ pl: 3, color: colors.grey[100] }}>{emp.Joining_Date || "N/A"}</TableCell>
                            <TableCell
                            sx={{
                                color:
                                emp.Status === "Active"
                                    ? colors.greenAccent[500]
                                    : emp.Status === "Inactive"
                                    ? colors.redAccent[500]
                                    : emp.Status === "Resigned"
                                    ? colors.blueAccent[500]
                                    : emp.Status === "On Leave"
                                    ? "blue"
                                    : colors.blueAccent[300],
                                fontWeight: "bold",
                                textTransform: "capitalize",
                            }}
                            >
                            {emp.Status || "N/A"}
                            </TableCell>
                            {/* <TableCell>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleViewDetails(emp.id)}
                                size="small"
                                >
                                Details
                                </Button>
                                <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleEditEmployee(emp.id, emp.Corporate_Email_ID)}
                                size="small"
                                >
                                Edit
                                </Button>
                                <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteEmployee(emp.id)}
                                size="small"
                                >
                                Delete
                                </Button>
                                <Button
                                variant="contained"
                                color="info"
                                onClick={() => handlePayslipEmployee(emp.id, emp.Corporate_Email_ID)}
                                size="small"
                                >
                                Payslip
                                </Button>
                            </Stack>
                            </TableCell> */}
                        </TableRow>

                        {(expandedRowId === emp.id || selectedEmployees.includes(emp.id)) && (
                            <TableRow key={`${emp.id}-expanded`}>
                            <TableCell colSpan={8}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleViewDetails(emp.id)}
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Details
                                </Button>

                                <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={() => handleEditEmployee(emp.id, emp.Corporate_Email_ID)}
                                    size="small"
                                    startIcon={<EditIcon />}
                                    sx={{ textTransform: 'none' }}
                                    >
                                    Edit
                                </Button>

                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleDeleteEmployee(emp.id)}
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Delete
                                </Button>

                                <Button
                                    variant="contained"
                                    color="info"
                                    onClick={() => handlePayslipEmployee(emp.id, emp.Corporate_Email_ID)}
                                    size="small"
                                    startIcon={<ReceiptIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Payslip
                                </Button>
                            </Stack>
                            </TableCell>
                            </TableRow>
                        )}
                        </React.Fragment>
                    ))}
                    </TableBody>

                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 20, 50]}
                component="div"
                count={filteredEmployees.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default AppEmpList;